import React from 'react';
import { Box, Typography } from '@mui/material';

export interface SourceCriterion {
    id?: string | null;
    group?: 'official_numbered' | 'official_unnumbered' | 'other_mandatory';
    criterion: string;
    evidence?: string;
    source_label?: string | null;
    source?: string;
    verification?: string;
    weight_pct?: number | null;
    weight_basis?: string;
    sources?: { document: string; part: string; quote: string; start?: number; end?: number }[];
    sub_criteria?: SourceCriterion[];
}

function CriterionRow({ item, depth = 0 }: { item: SourceCriterion; depth?: number }) {
    return <Box sx={{ ml: depth ? 2 : 0, mb: 0.6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                {item.id ? `${item.id}: ` : '• '}{item.criterion}
            </Typography>
            {item.weight_pct != null && <Typography variant="body2" sx={{ fontSize: 'inherit', flexShrink: 0 }}>
                {item.weight_pct}%{item.weight_basis === 'parent' ? ' of parent' : ''}
            </Typography>}
        </Box>
        {item.evidence && <Typography variant="caption" color="text.secondary">Evidence: {item.evidence}</Typography>}
        {item.verification === 'needs_review' && <Typography variant="caption" color="warning.main" display="block">Needs review</Typography>}
        {(item.sources?.length ?? 0) > 0 && <Box component="details" sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.25 }}>
            <summary>Source{item.source_label ? ` (${item.source_label})` : ''}</summary>
            {item.sources!.map((source, index) => <Box key={index} sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                <strong>{source.document.split('/').pop()} / {source.part}</strong>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{source.quote}</Box>
            </Box>)}
        </Box>}
        {item.sub_criteria?.map((child, index) => <CriterionRow key={index} item={child} depth={depth + 1} />)}
    </Box>;
}

export default function CriteriaEntries({ items, type }: { items: SourceCriterion[]; type: 'EK' | 'ZK' }) {
    const groups = [
        { key: undefined, title: '' },
        { key: 'official_numbered', title: `Official ${type}s` },
        { key: 'official_unnumbered', title: `Unnumbered ${type}s` },
        { key: 'other_mandatory', title: 'Other mandatory requirements' },
    ];
    return <>{groups.map(group => {
        const entries = items.filter(item => item.group === group.key);
        if (!entries.length) return null;
        return <Box key={group.key ?? 'legacy'} sx={{ mb: 1 }}>
            {group.title && <Typography variant="caption" sx={{ fontWeight: 600 }}>{group.title}</Typography>}
            {entries.map((item, index) => <CriterionRow key={index} item={item} />)}
        </Box>;
    })}</>;
}
